import { type JSX, type Ref, useImperativeHandle, useState } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { ContractEditor } from "./ContractEditor";
import {
  type ContractEditorDataProps,
  contractEditorDataSchema,
} from "./ContractEditor.schema";
import { ContractEditorDialog } from "./ContractEditorDialog";

export type ContractEditorHandle = {
  open: (contractId?: number | null) => void;
  hide: () => void;
};

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    "contract-editor": {
      props: ContractEditorDataProps;
      handle: ContractEditorHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-contract-editor": HTMLElement & ContractEditorHandle;
  }
}

export function RWCContractEditor({
  ref,
  projectId,
  contractId: initialContractId,
  contained,
  open: initialOpen,
}: ContractEditorDataProps & {
  ref?: Ref<ContractEditorHandle>;
}): JSX.Element {
  const host = useIslandHost();

  const [contractId, setContractId] = useState<number | null>(
    initialContractId,
  );

  const [open, setOpen] = useState(initialOpen);

  useImperativeHandle(
    ref,
    () => ({
      open: (next = null) => {
        setContractId(next ?? null);
        setOpen(true);
      },
      hide: () => setOpen(false),
    }),
    [],
  );

  function emit(type: string, detail: Record<string, unknown> = {}) {
    host?.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
  }

  const onSaved = (savedId: number) =>
    emit("contract:saved", { contractId: savedId });

  const onCancel = () => emit("contract:cancel");

  if (contained) {
    return (
      <ContractEditor
        projectId={projectId}
        contractId={contractId}
        onSaved={onSaved}
        onCancel={onCancel}
      />
    );
  }

  return (
    <ContractEditorDialog
      open={open}
      onOpenChange={setOpen}
      projectId={projectId}
      contractId={contractId}
      onSaved={onSaved}
      onCancel={onCancel}
    />
  );
}

componentManager.register(
  rwc("contract-editor", {
    component: RWCContractEditor,
    dataSchema: contractEditorDataSchema,
  }),
);
